import { useEffect, useMemo, useState } from "react";
import { http, toastApiError, postDownload } from "../../lib/api";
import { toast } from "../../lib/notify";
import { X, FileDown } from "lucide-react";
import ColumnPicker from "./ColumnPicker";
import SelectedColumns from "./SelectedColumns";
import TemplateBar from "./TemplateBar";

// Sütun şablonlu Excel dışa aktarımı: sol tarafta katalog (gruplu), sağda seçilen sütunlar (sıra + başlık düzenlenebilir); şablon isimle kaydedilir
export default function ExportTemplateDialog({ open, onClose, kind, title, endpoint, filename, testid = "exp", extraBody = {}, note }) {
  const [catalog, setCatalog] = useState([]);
  const [cols, setCols] = useState([]);           // [{key, label}] seçim sırasıyla
  const [tpls, setTpls] = useState([]);
  const [tplId, setTplId] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const loadTpls = () => http.get("/export-templates", { params: { kind } }).then((r) => setTpls(r.data)).catch(toastApiError);
  useEffect(() => {
    if (!open) return;
    http.get(`/export-columns/${kind}`).then((r) => { setCatalog(r.data); setCols(r.data.map(({ key, label }) => ({ key, label }))); }).catch(toastApiError);
    loadTpls(); setTplId(""); setName("");
  }, [open, kind]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!open) return; const k = (e) => { if (e.key === "Escape") onClose(); }; window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [open, onClose]);
  const groups = useMemo(() => { const m = new Map(); catalog.forEach((c) => { if (!m.has(c.group)) m.set(c.group, []); m.get(c.group).push(c); }); return [...m.entries()]; }, [catalog]);
  if (!open) return null;

  const toggle = (c) => setCols((s) => (s.some((x) => x.key === c.key) ? s.filter((x) => x.key !== c.key) : [...s, { key: c.key, label: c.label }]));
  const setMany = (items, on) => setCols((s) => (on ? [...s, ...items.filter((c) => !s.some((x) => x.key === c.key)).map(({ key, label }) => ({ key, label }))] : s.filter((x) => !items.some((c) => c.key === x.key))));
  const applyTpl = (id) => { setTplId(id); const t = tpls.find((x) => x.id === id); if (t) { setCols(t.columns); setName(t.name); } };
  const saveTpl = async () => {
    if (!name.trim()) return toast.error("Şablon adı girin");
    if (!cols.length) return toast.error("En az bir sütun seçin");
    try { const r = await http.post("/export-templates", { kind, name: name.trim(), columns: cols }); toast.success("Şablon kaydedildi"); await loadTpls(); setTplId(r.data.id); } catch (e) { toastApiError(e); }
  };
  const delTpl = async () => { if (!tplId) return; try { await http.delete(`/export-templates/${tplId}`); setTplId(""); setName(""); loadTpls(); toast.success("Şablon silindi"); } catch (e) { toastApiError(e); } };
  const download = async () => {
    if (!cols.length) return toast.error("En az bir sütun seçin");
    setBusy(true);
    try { await postDownload(endpoint, { ...extraBody, columns: cols }, `${filename}-${new Date().toISOString().slice(0, 10)}.xlsx`); toast.success("Excel indirildi"); } catch (e) { toastApiError(e); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid={`${testid}-dialog`}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <FileDown className="w-4 h-4 text-emerald-700" /><span className="text-sm font-bold">{title}</span>
          <span className="text-[10px] text-slate-500">{cols.length}/{catalog.length} sütun</span>
          <button type="button" onClick={onClose} className="ml-auto p-1 rounded hover:bg-slate-100" data-testid={`${testid}-close`}><X className="w-4 h-4" /></button>
        </div>
        <TemplateBar tpls={tpls} tplId={tplId} name={name} setName={setName} onApply={applyTpl} onSave={saveTpl} onDelete={delTpl} testid={testid}
          onAll={() => setCols(catalog.map(({ key, label }) => ({ key, label })))} onNone={() => setCols([])} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[26rem] overflow-y-auto pr-1">
          <ColumnPicker groups={groups} cols={cols} toggle={toggle} setMany={setMany} testid={testid} />
          <SelectedColumns cols={cols} setCols={setCols} testid={testid} />
        </div>
        <div className="flex items-center justify-end gap-2">
          <span className="text-[11px] text-slate-500 mr-auto" data-testid={`${testid}-note`}>{note || "Sağdaki liste Excel sütun sırasıdır; başlıkları yazarak değiştirebilirsiniz."}</span>
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-md border border-slate-300 bg-white text-xs font-semibold">Kapat</button>
          <button type="button" onClick={download} disabled={busy || !cols.length} data-testid={`${testid}-download`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-800 disabled:opacity-50">
            <FileDown className="w-3.5 h-3.5" /> {busy ? "Hazırlanıyor…" : `Excel İndir (${cols.length} sütun)`}
          </button>
        </div>
      </div>
    </div>
  );
}
